// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/ShowcaseSimplified.tsx
// Version: v1.1.0
// Beschreibung: Services-Übersicht mit bereinigter Service-Liste (nur definierte Keys)

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useLanguage } from "../lang/index.lang.tsx";

// Item-Definition
export interface ShowcaseItem {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  link: string;  
}

// Props der Komponente (minimal)
interface ShowcaseSimplifiedProps {
  className?: string;
}

const ShowcaseSimplified: React.FC<ShowcaseSimplifiedProps> = ({
  className = "",
}) => {
  const { t } = useLanguage();
  const [activeItem, setActiveItem] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchOffset, setTouchOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const navigate = useNavigate();
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Services aus de.json extrahieren und alphabetisch sortieren
  // NUR Keys die in de.json definiert sind!
  const items = useMemo(() => {
    const serviceKeys = [
      'general-planning',
      'design-build',
      'construction-services',
      'workplace-consulting',
      'estata-retail',
      'estata-commercial',
      'estata-residental',
      'interior-fitout',
      'hklse',
      'building-refurbishment',
      'design-build-repeat',
      'due-diligence',
      'energy-advisory',
      'heating-energy-services',
      'development-strategies',
      'fire-protection',
      'fire-concepts',
      'fire-regulations',
      'fire-safety-officers',
      'fire-construction-supervision'
    ];

    const serviceItems: ShowcaseItem[] = serviceKeys
      .map((key, index) => ({
        id: index + 1,
        title: t(`services.${key}.title`),
        subtitle: t(`services.${key}.description.short`),
        description: t(`services.${key}.description.long`),
        link: `/services/${key}`
      }))
      .sort((a, b) => a.title.localeCompare(b.title, 'de'));

    return serviceItems;
  }, [t]);

  // Höhe des aktiven Items messen (nur Desktop)
  useEffect(() => {
    const measureHeight = () => {
      const activeRef = itemRefs.current[activeItem];
      if (activeRef && window.innerWidth >= 1024) {
        const height = activeRef.getBoundingClientRect().height;
        setContainerHeight(height);
      }
    };

    measureHeight();
    const timer = setTimeout(measureHeight, 100);
    window.addEventListener('resize', measureHeight);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', measureHeight);
    };
  }, [activeItem, items]);

  // Handler beim Klick auf ein Item
  const handleItemClick = (index: number): void => {
    setActiveItem(index);
    
    // Im Mobile- und Tablet-Modus Modal öffnen
    if (window.innerWidth < 1024) {
      setIsModalOpen(true);
      document.body.style.overflow = 'hidden';
    }
  };

  // Modal schließen
  const closeModal = (): void => {
    setIsModalOpen(false);
    setTouchOffset({ x: 0, y: 0 });
    document.body.style.overflow = '';
  };

  // Touch-Start Handler
  const handleTouchStart = (e: React.TouchEvent): void => {
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
  };

  // Touch-Move Handler
  const handleTouchMove = (e: React.TouchEvent): void => {
    if (!touchStart) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    
    // Nach unten, rechts oder links erlauben
    if (deltaY > 0 || deltaX !== 0) {
      setTouchOffset({ x: deltaX, y: Math.max(0, deltaY) });
    }
  };

  // Touch-End Handler
  const handleTouchEnd = (): void => {
    const threshold = 100;
    
    // Wenn Swipe-Distanz größer als Threshold, Modal schließen
    // Nach unten, rechts oder links
    if (touchOffset.y > threshold || Math.abs(touchOffset.x) > threshold) {
      closeModal();
    } else {
      // Zurück zur Ausgangsposition
      setTouchOffset({ x: 0, y: 0 });
    }
    
    setTouchStart(null);
  };

  // ESC-Taste zum Schließen
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen]);

  return (
    <div className={`w-full bg-black text-white ${className}`}>
      {/* Heading Bereich */}
      <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 mb-8 md:mb-12 lg:mb-16">
        <div className="text-white text-xl md:text-1xl lg:text-2xl mb-2">
          {t('services.heading.subtitle')}
        </div>
        <div className="text-4xl md:text-5xl lg:text-7xl font-bold">
          {t('services.heading.AllServices')}
        </div>
      </div>

      {/* Hauptinhalt */}
      <div className="max-w-[1700px] mx-auto mb-[150px] md:mb-[150px] lg:mb-[300px] px-4 sm:px-6 lg:px-8">
        
        {/* Links - Mobile/Tablet: vertikal / Desktop: horizontal */}
        <div className="flex flex-col lg:flex-row lg:flex-wrap gap-6 md:gap-8 lg:gap-12 mb-12 md:mb-16 lg:mb-20">
          {items.map((item, index) => (
            <button
              key={item.id}
              onClick={() => handleItemClick(index)}
              type="button"
              className="relative group text-left"
              aria-pressed={index === activeItem}
            >
              <div
                className={`text-2xl md:text-3xl lg:text-3xl font-bold transition-colors duration-200 leading-[1.1] lg:leading-[0.03] ${
                  index === activeItem
                    ? "text-white underline"
                    : "text-white/30 group-hover:text-white"
                }`}
              >
                {item.title}
              </div>
            </button>
          ))}
        </div>

        {/* Desktop: Descriptions mit Fade-In */}
        <div className="hidden lg:block relative">
          {items.map((item, index) => (
            <div
              key={item.id}
              ref={(el) => { itemRefs.current[index] = el; }}
              className={`transition-opacity duration-500 ${
                index === activeItem 
                  ? 'relative opacity-100' 
                  : 'absolute top-0 left-0 w-full opacity-0 pointer-events-none'
              }`}
            >
              <p className="text-white text-2xl md:text-3xl lg:text-3xl font-bold !leading-[1.2]">
                {item.subtitle}
              </p>
              <p className="text-white text-xl md:text-1xl lg:text-2xl mt-3 md:mt-7 lg:mt-8 font-serif">
                {item.description}
              </p>
              {item.link && item.link.trim() !== "" && (
                <a
                  href={item.link}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.link);
                  }}
                  className="border border-white text-white px-8 py-2 hover:bg-white hover:text-black mt-6 md:mt-7 lg:mt-8 transition-all duration-200 inline-block cursor-pointer"
                >
                  {t('services.heading.learnMore')}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile/Tablet: Modal Sheet mit Swipe-to-Dismiss */}
      {isModalOpen && typeof document !== 'undefined' && createPortal(
        <div className="lg:hidden">
          {/* Backdrop - immer fixed zur Viewport */}
          <div 
            className="fixed inset-0 bg-black/80 z-[9998]"
            style={{ position: 'fixed' }}
            onClick={closeModal}
          />
          
          {/* Modal Sheet - immer fixed zur Viewport */}
          <div
            className="fixed bg-black border-t border-white/20 z-[9999] rounded-t-3xl overflow-y-auto"
            style={{
              position: 'fixed',
              top: '100px',
              left: 0,
              right: 0,
              bottom: 0,
              transform: `translate(${touchOffset.x}px, ${touchOffset.y}px)`
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Swipe Indicator */}
            <div className="flex justify-center pt-4 pb-2">
              <div className="w-12 h-1 bg-white/30 rounded-full"></div>
            </div>

            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors z-10"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            {/* Content */}
            <div className="px-6 pb-8 pt-4">
              <p className="text-white text-2xl font-bold !leading-[1.2] mb-4">
                {items[activeItem].subtitle}
              </p>
              <p className="text-white text-lg font-serif mb-6">
                {items[activeItem].description}
              </p>
              {items[activeItem].link && items[activeItem].link.trim() !== "" && (
                <a
                  href={items[activeItem].link}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(items[activeItem].link);
                    closeModal();
                  }}
                  className="border border-white text-white px-8 py-2 hover:bg-white hover:text-black transition-all duration-200 inline-block cursor-pointer"
                >
                  {t('services.heading.learnMore')}
                </a>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ShowcaseSimplified;